import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Calendar,
  CheckCircle2,
  Clock,
  UserCheck,
  Layers,
  PhoneCall,
  Check,
} from "lucide-react";

import Seo from "../../Components/SEO/Seo";
import Container from "../../Components/Container/Container";
import Button from "../../Components/Button/Button";
import {
  QUICK_VIEW_STRATEGIES,
  ROI_DELIVERY_APPROACHES,
  BOTTOM_FEATURE_COLUMNS,
} from "../../Data/b2bAppointmentData";

import "../../Style/services/data-validation.css";
import "../../Style/services/data-verification.css";
import "../../Style/services/b2b-appointment.css";

const cardHover = {
  initial: { y: 0, opacity: 0.95 },
  hover: { y: -6, opacity: 1, transition: { duration: 0.25, ease: "easeOut" } },
};

export default function B2BAppointment() {
  const [activeMatrixTab, setActiveMatrixTab] = useState(0);

  const handleConsultation = () => {
    window.history.pushState({}, "", "/contact");
    window.dispatchEvent(new Event("popstate"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="validation-page appt-page">
      <Seo
        title="B2B Appointment Setting Services | Conectify Tech"
        description="Book high-converting meetings with verified enterprise decision makers. Multi-touch call targeting, 3-month lead exclusivity, and fixed price models."
      />

      {/* Hero Section */}
      <section className="val-hero appt-hero">
        <Container className="val-hero__container">
          <motion.div
            className="val-hero__content"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >
            <div className="section-eyebrow eye">
              <Sparkles size={14} />
              <span>Calibrated Sales Meetings</span>
            </div>

            <h1 className="val-hero__title">
              B2B Appointment{" "}
              <span className="text-gradient">Setting Services</span>
            </h1>

            <p className="val-hero__lead">
              Our seasoned appointment setting experts know the triggers that lead to high-quality
              contacts and meetings that your sales team recognizes and closes. Given that Conectify Tech’s
              B2B has improved call targeting techniques, our meeting preparation efforts generate a
              high ROI for you while lowering business costs.
            </p>

            <p className="val-hero__sublead">
              We support our high-volume appointment scheduling programs and analytics that provide
              the details of every call we make.
            </p>

            <div className="val-hero__tags">
              <span className="val-tag">
                <Calendar size={13} className="text-primary" />
                <span>Confirmed Discovery Calls</span>
              </span>
              <span className="val-tag">
                <Layers size={13} className="text-primary" />
                <span>Multi-Layer Call Targeting</span>
              </span>
              <span className="val-tag">
                <Clock size={13} className="text-primary" />
                <span>3-Month Exclusive Lead Hold</span>
              </span>
              <span className="val-tag">
                <CheckCircle2 size={13} className="text-success" />
                <span>Fixed Price Transparent Model</span>
              </span>
            </div>

            <div className="val-hero__actions">
              <Button icon={<ArrowRight size={18} />} onClick={handleConsultation}>
                Schedule A Consultation
              </Button>
              <Button variant="secondary" onClick={handleConsultation}>
                View Target Group Scope
              </Button>
            </div>
          </motion.div>

          {/* Interactive Booking Calendar HUD */}
          <motion.div
            className="val-hero__card-wrap"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, delay: 0.15 }}
          >
            <div className="appt-calendar-hud">
              <div className="appt-calendar-hud__top">
                <div className="flex items-center gap-2">
                  <Calendar size={18} className="text-primary" />
                  <span className="font-bold text-sm">Targeted Meeting Dispatch</span>
                </div>
                <span className="hud-live-tag">
                  <span className="live-dot"></span> Active Call Queue
                </span>
              </div>

              {/* Sample Scheduled Meeting Items */}
              <div className="appt-meeting-queue">
                <div className="appt-meeting-card">
                  <div className="appt-meeting-time">10:00 AM</div>
                  <div className="appt-meeting-details">
                    <h4>Chief Technology Officer</h4>
                    <p>Fintech SaaS • 800+ Employees</p>
                  </div>
                  <span className="appt-meeting-badge">SQL Confirmed</span>
                </div>

                <div className="appt-meeting-card appt-meeting-card--highlight">
                  <div className="appt-meeting-time">02:30 PM</div>
                  <div className="appt-meeting-details">
                    <div className="flex items-center gap-1">
                      <UserCheck size={14} className="text-primary" />
                      <h4>VP of Global Procurement</h4>
                    </div>
                    <p>Logistics Network • Miami, FL</p>
                  </div>
                  <span className="appt-meeting-badge appt-meeting-badge--green">
                    Ready to Close
                  </span>
                </div>

                <div className="appt-meeting-card">
                  <div className="appt-meeting-time">04:15 PM</div>
                  <div className="appt-meeting-details">
                    <h4>Director of Infrastructure</h4>
                    <p>Healthcare Systems • Dallas, TX</p>
                  </div>
                  <span className="appt-meeting-badge">MQL Verified</span>
                </div>
              </div>

              <div className="appt-calendar-hud__footer">
                <div className="stat">
                  <span className="num">50M+</span>
                  <span className="lbl">Verified Reach</span>
                </div>
                <div className="stat">
                  <span className="num">90 Days</span>
                  <span className="lbl">Exclusivity</span>
                </div>
                <div className="stat">
                  <span className="num">100%</span>
                  <span className="lbl">Survey Backed</span>
                </div>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Quick View for Appointment Setting */}
      <section className="appt-quickview">
        <Container>
          <div className="appt-split-box">
            {/* Interactive Cadence Orbit Diagram */}
            <div className="appt-cadence-visual">
              <div className="cadence-orbit">
                <div className="cadence-orbit__ring"></div>
                <div className="cadence-center-node">
                  <PhoneCall size={28} className="text-primary" />
                  <span>Appointment Setting</span>
                </div>

                {/* 4 Telemetry Nodes orbiting the center */}
                <div className="orbit-node orbit-node--top">
                  <span>Management</span>
                </div>
                <div className="orbit-node orbit-node--right">
                  <span>Analytics</span>
                </div>
                <div className="orbit-node orbit-node--bottom">
                  <span>Reporting</span>
                </div>
                <div className="orbit-node orbit-node--left">
                  <span>Pipeline</span>
                </div>
              </div>
            </div>

            {/* Quick View Content & Strategies */}
            <div className="appt-split-box__content">
              <div className="section-eyebrow">Tactical Execution</div>
              <h2>Quick view for Appointment Setting</h2>
              <p className="lead-text">
                Creating appointments is never easy. We attain the end goal by following different
                strategies. Some of the highlights of our techniques and tools:
              </p>

              <div className="appt-strategies-grid">
                {QUICK_VIEW_STRATEGIES.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="appt-strategy-chip">
                      <Icon size={16} className="text-primary" />
                      <span>{item.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* How We Deliver Higher Business ROI */}
      <section className="appt-roi">
        <Container>
          <div className="section-head text-center">
            <span className="section-eyebrow">Extended Team Synergy</span>
            <h2 className="section-title">How We Deliver Higher Business ROI</h2>
            <p className="section-desc max-w-2xl mx-auto">
              We attempt to deliver real added value and act as an extended team. Our value is based
              on the following approaches:
            </p>
          </div>

          <div className="appt-roi__grid">
            {ROI_DELIVERY_APPROACHES.map((appr) => {
              const Icon = appr.icon;
              return (
                <motion.div
                  key={appr.title}
                  className="val-why-card appt-roi-card"
                  variants={cardHover}
                  initial="initial"
                  whileHover="hover"
                >
                  <div className="val-why-card__icon">
                    <Icon size={22} />
                  </div>
                  <h4>{appr.title}</h4>
                  <p>{appr.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Cyber-Bento Matrix */}
      <section className="appt-matrix-section">
        <Container>
          <div className="section-head text-center">
            <span className="section-eyebrow">Enterprise Core Capabilities</span>
            <h2 className="section-title">Precision Outreach Architecture</h2>
            <p className="section-desc max-w-2xl mx-auto">
              Inspect our high-volume operational frameworks built for verified qualification,
              zero-friction suppression, and enterprise-grade scale.
            </p>
          </div>

          <div className="appt-matrix-shell">
            {/* Left Controller Tabs */}
            <div className="appt-matrix-nav">
              {BOTTOM_FEATURE_COLUMNS.map((col, idx) => {
                const isSelected = activeMatrixTab === idx;
                return (
                  <button
                    key={col.id}
                    type="button"
                    className={`appt-matrix-trigger ${isSelected ? "is-active" : ""}`}
                    onClick={() => setActiveMatrixTab(idx)}
                  >
                    <div className="appt-matrix-trigger__indicator">
                      <span>0{idx + 1}</span>
                    </div>
                    <div className="appt-matrix-trigger__text">
                      <h4>{col.title}</h4>
                      <p>{col.subtitle}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Right Display Console */}
            <div className="appt-matrix-display">
              <AnimatePresence mode="wait">
                {(() => {
                  const activeCol = BOTTOM_FEATURE_COLUMNS[activeMatrixTab];
                  return (
                    <motion.div
                      key={activeCol.id}
                      className="appt-matrix-screen"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="appt-matrix-screen__header">
                        <div className="badge-wrapper">
                          <span className="live-pulse"></span>
                          <span className="screen-tag">Live System Module</span>
                        </div>
                        <span className="screen-counter">Phase 0{activeMatrixTab + 1} / 03</span>
                      </div>

                      <div className="appt-matrix-screen__hero">
                        <h3>{activeCol.title}</h3>
                        <p>{activeCol.subtitle} — Calibrated outreach infrastructure with active validation protocols.</p>
                      </div>

                      {/* Capabilities Grid */}
                      <div className="appt-matrix-screen__checklist">
                        {activeCol.points.map((pt, i) => (
                          <div key={i} className="checklist-card">
                            <div className="checklist-bullet">
                              <Check size={14} />
                            </div>
                            <span>{pt}</span>
                          </div>
                        ))}
                      </div>

                      {/* Bottom Status Bar */}
                      <div className="appt-matrix-screen__footer">
                        <div className="metric-chip">
                          <span className="metric-chip__label">Status</span>
                          <span className="metric-chip__val text-success">100% Operational</span>
                        </div>
                        <div className="metric-chip">
                          <span className="metric-chip__label">SLA Protection</span>
                          <span className="metric-chip__val">90 Days Active</span>
                        </div>
                        <div className="metric-chip">
                          <span className="metric-chip__label">Data Integrity</span>
                          <span className="metric-chip__val text-primary">Multi-Tier QA</span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })()}
              </AnimatePresence>
            </div>
          </div>
        </Container>
      </section>

      {/* Bottom CTA */}
      <section className="b2b-cta">
        <Container>
          <div className="b2b-cta__card">
            <h2>Accelerate Your Deal Velocity with Scaled Appointment Setting</h2>
            <p>
              Connect with vetted prospects ready to evaluate your enterprise solutions. Eliminate cold
              calling friction and fill your reps' calendars this quarter.
            </p>
            <Button icon={<ArrowRight size={18} />} onClick={handleConsultation}>
              Book a Strategy Call
            </Button>
          </div>
        </Container>
      </section>
    </div>
  );
}